import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { PageHeader, Card } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed } from "@/lib/format";
import ReportPageWrap from "@/components/reports/ReportPageWrap";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";
import { printTipPoolReport } from "@/lib/miscReportPrint";
import PaymentMethodFilter from "@/components/reports/PaymentMethodFilter";
import { formatReportFilterLine } from "@/lib/reportFilters";

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

export default function TipPoolReport() {
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [run, setRun] = useState(false);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["tip-pool-report", branchId, fromDate, toDate, paymentMethod],
    queryFn: async () => {
      const params = { ...reportParams, date_from: fromDate, date_to: toDate };
      if (paymentMethod !== "all") params.payment_method = paymentMethod;
      const { data: res } = await api.get("/tips/pool-report", { params });
      return res;
    },
    enabled: Boolean(canQuery && run),
  });

  return (
    <ReportPageWrap>
      <PageHeader
        title="Tip & Service Charge Pool"
        subtitle="Tips and service charge totals by staff (bill owner) for payroll sharing"
        actions={
          <ReportExportButtons
            disabled={!data}
            title="Tip & Service Charge Pool"
            filename="tip-pool"
            period={formatReportFilterLine({ fromDate, toDate, paymentMethod })}
            onPrint={() =>
              printTipPoolReport(
                { ...data, from_date: fromDate, to_date: toDate, payment_method: paymentMethod },
                { branchId, allBranches },
              )
            }
          />
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label>From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <PaymentMethodFilter value={paymentMethod} onChange={setPaymentMethod} />
          <Button
            onClick={() => {
              setRun(true);
              refetch();
            }}
            disabled={isFetching}
          >
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run report"}
          </Button>
        </div>
      </Card>

      {data && (
        <>
          <ReportKpiStrip
            items={[
              { label: "Bills", value: data.totals?.bill_count },
              { label: "Tips", value: aed(data.totals?.tip_total) },
              { label: "Pool total", value: aed(data.totals?.pool_total), emphasize: true },
            ]}
          />

          <ReportTableCard title="By staff">
            <table>
              <thead>
                <tr>
                  <th className="text-left">Staff</th>
                  <th className="text-right">Bills</th>
                  <th className="text-right">Net sales</th>
                  <th className="text-right">Tips</th>
                  <th className="text-right">Pool</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows ?? []).map((row) => (
                  <tr key={row.user_id ?? row.staff_name}>
                    <td className="font-medium">{row.staff_name}</td>
                    <td className="text-right">{row.bill_count}</td>
                    <td className="text-right font-mono">{aed(row.net_sales)}</td>
                    <td className="text-right font-mono">{aed(row.tip_total)}</td>
                    <td className="text-right font-mono font-semibold">{aed(row.pool_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportTableCard>
        </>
      )}
    </ReportPageWrap>
  );
}
